import {
  Brand,
  getAllProducts,
  Products,
  type Items
} from '@fe-sdk/cbx-products'
import {
  groupBy as cbxGroupBy,
  productMapper as cbxProductMapper,
  sortItemsBySequenceNumber,
  sortVariantsBySequenceNumber
} from '.'
import {
  Asset,
  type ProductDataVariant,
  TagGroup
} from '../../../../services/product/product-data'
import { type GroupedItem } from '../models'
import { type ProductDataType } from '../../common/AddToCart/types'
import { AkamaiApiHeaders } from '../../../../utils/api-headers'
import { isFixedProductSet } from '../../../../utils/product-set'

const toProductDataType = (item: Products): ProductDataType => {
  const assets = item.assets?.map((asset) => ({
    assetFullUrl: asset?.assetFullUrl ?? '',
    assetRelativePath: (asset as Asset)?.assetRelativePath ?? '',
    assetType: asset?.assetType ?? ''
  }))

  return {
    ...item,
    globalArticleName: item.globalArticleName ?? '',
    globalArticleCode: item.globalArticleCode ?? '',
    price: {
      isServiceFeeIncluded: item.price?.isServiceFeeIncluded ?? false,
      priceChannel: item.price?.priceChannel ?? '',
      priceCurrency: item.price?.priceCurrency ?? '',
      grossPrice: item.price?.grossPrice ?? 0,
      ...(typeof item.price?.subscriptionPrice === 'number'
        ? { subscriptionPrice: item.price.subscriptionPrice }
        : {}),
      ...(item.price?.priceDiscount
        ? { priceDiscount: item.price.priceDiscount }
        : {}),
      ...(item.price?.multiBuyPrice
        ? { multiBuyPrice: item.price.multiBuyPrice }
        : {})
    },
    brand: item.brand ?? '',
    categoryIdentifier: item.categoryIdentifier ?? '',
    nuance: {
      code: item.nuance?.code ?? '',
      description: item.nuance?.description ?? '',
      group: {
        hexCode: item.nuance?.group?.hexCode ?? '',
        code: item.nuance?.group?.code ?? '',
        name: item.nuance?.group?.name ?? ''
      },
      hexCode: item.nuance?.hexCode ?? '',
      name: item.nuance?.name ?? ''
    },
    stock: {
      stockLevel: item.stock?.stockLevel ?? 0,
      stockLevelStatus: item.stock?.stockLevelStatus ?? ''
    },
    assets,
    pmiInternalProductCode: item.pmiInternalProductCode ?? '',
    deviceVersion: item.deviceVersion ?? '',
    preOrderUntilDate: item.preOrderUntilDate ?? '',
    productFamily: item.productFamily ?? '',
    minimumOrderQuantity: item.minimumOrderQuantity,
    relatedPackCartonCodes: item?.relatedPackCartonCodes
  }
}

type ProductsConfig = {
  market: string
  channel: string
  xChannel: string
  loggerEnabled: boolean
  locale: string
  cbxUrl: string
  brand?: Brand
  touchpoint?: string
}

interface FetchProductsDataParams extends AkamaiApiHeaders {
  config: ProductsConfig
  superTags: string
  groupBy?: {
    productGroups: {
      productFamily: string
      productProperty: string
    }[]
    except: string[]
  }
}

export const fetchProductsData = async ({
  config,
  superTags,
  groupBy,
  customHeaders
}: FetchProductsDataParams) => {
  const superTagsArray = superTags
    ? superTags.split(',').map((tag) => tag.trim())
    : undefined

  const sdkResponse = await getAllProducts({
    baseURL: config?.cbxUrl ?? '',
    brand: config?.brand ?? 'IQOS',
    channel:
      config?.channel?.toLowerCase() === 'web'
        ? 'web'
        : (config?.channel ?? ''),
    xChannel: config?.xChannel || 'WEBSITE',
    countryCode: config?.market?.toUpperCase() ?? '',
    market: config?.market ?? '',
    locale: config?.locale,
    superTags: superTagsArray,
    touchpoint: config?.touchpoint,
    currentPage: 1,
    pageSize: 100,
    forwardedHost: 'default',
    ...(customHeaders ? { customHeaders } : {})
  })

  if ('error' in sdkResponse || !sdkResponse.data) {
    return {
      data: {
        gcodes: [],
        facets: [],
        items: []
      }
    }
  }

  const sdkData = sdkResponse.data as Items

  const gcodes = sdkData.items.map(({ globalArticleCode }) => globalArticleCode)

  // Include inner product codes from fixed product sets for stock fetching
  const fixedProductSetCodes = sdkData.items
    .filter(
      (item) =>
        item.productFamily === 'ProductSet' &&
        isFixedProductSet(
          (item as unknown as ProductDataVariant).productSetReferences
        )
    )
    .flatMap(
      (item) =>
        (item as unknown as ProductDataVariant).productSetReferences?.flatMap(
          (ref) => ref.globalArticleCode ?? []
        ) ?? []
    )

  const allGcodes = [...gcodes, ...fixedProductSetCodes]

  // middleware
  const facets = sdkData.facets.reduce(
    (acc: TagGroup[], { tagGroups = [] }) => [...acc, ...tagGroups],
    []
  )
  // Transform Products[] to ProductDataType[]
  const itemsForGroupBy = (sdkData?.items ?? []).map(toProductDataType)

  const items = Object.values(
    cbxGroupBy(
      itemsForGroupBy as ProductDataVariant[],
      cbxProductMapper,
      groupBy
    )
  )
    .map((item: GroupedItem) => ({
      ...item,
      variants: item.variants.slice().sort(sortVariantsBySequenceNumber)
    }))
    .slice()
    .sort(sortItemsBySequenceNumber)

  return {
    data: {
      gcodes: allGcodes,
      facets,
      items
    }
  }
}
